import { useDeferredValue, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCopy,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { parseUrl, type ParsedUrl } from '../lib/urlParse';
import { ToolHeader } from '../components/ToolHeader';

const FIELD_ROWS: ReadonlyArray<{ key: keyof ParsedUrl; label: string; mask?: boolean }> = [
  { key: 'protocol', label: 'protocol' },
  { key: 'username', label: 'username' },
  { key: 'password', label: 'password', mask: true },
  { key: 'host', label: 'host' },
  { key: 'hostname', label: 'hostname' },
  { key: 'port', label: 'port' },
  { key: 'pathname', label: 'pathname' },
  { key: 'search', label: 'search' },
  { key: 'hash', label: 'hash' },
];

/**
 * `/tools/network/url-parse` — structural decomposition of a URL.
 *
 * Layout: input → 9 atom rows + a query table + two copy buttons
 * (canonical href / JSON snapshot). The IDN auto-conversion and
 * auto-protocol prepend are surfaced as inline notices so users can't
 * miss them.
 */
export default function UrlParseTool(): JSX.Element {
  const [input, setInput] = useState('');
  const deferred = useDeferredValue(input);
  const result = useMemo(() => parseUrl(deferred), [deferred]);
  const [showPassword, setShowPassword] = useState(false);

  const jsonSnapshot = useMemo(() => {
    if (!result.isValid) return '';
    const { protocol, username, password, hostname, port, pathname, search, hash, query } =
      result;
    return JSON.stringify(
      { protocol, username, password, hostname, port, pathname, search, hash, query },
      null,
      2,
    );
  }, [result]);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <ToolHeader
        toolId="url-parse"
        category="network"
        description="基于浏览器原生 URL 解析器，结构化拆出 protocol / host / path / query / hash。query 同时显示原始与解码值。"
      />

      <section className="card-pad flex flex-col gap-2">
        <header className="text-[11px] uppercase tracking-wide text-ink-dim">URL</header>
        <textarea
          className="input min-h-[64px] font-mono text-[12.5px]"
          placeholder="https://user:pass@api.example.com:8080/v1/users?q=hello&filter=name#section1"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          spellCheck={false}
        />
        {input.trim().length > 0 && !result.isValid && (
          <p className="inline-flex items-center gap-1 text-sm text-rose-600 dark:text-rose-400">
            <XCircle className="h-3.5 w-3.5" />
            {result.error}
          </p>
        )}
        {result.isValid && result.autoProtocolAdded && (
          <p className="inline-flex items-center gap-1 text-[12px] text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-3 w-3" />
            已自动补 <code className="font-mono">https://</code> 前缀解析。
          </p>
        )}
      </section>

      {result.isValid && (
        <>
          <section className="card-pad">
            <header className="mb-2 text-[11px] uppercase tracking-wide text-ink-dim">
              结构
            </header>
            <dl className="grid grid-cols-1 gap-y-1 sm:grid-cols-[7em_1fr] sm:gap-x-3 text-[12.5px]">
              {FIELD_ROWS.map((row) => {
                const v = String(result[row.key] ?? '');
                const masked = row.mask && v.length > 0 && !showPassword;
                return (
                  <FieldRow
                    key={row.key}
                    label={row.label}
                    value={v}
                    rendered={masked ? '•'.repeat(Math.max(4, v.length)) : v}
                    extra={
                      row.mask && v.length > 0 ? (
                        <button
                          type="button"
                          className="text-[11px] text-ink-muted hover:text-accent"
                          onClick={() => setShowPassword((s) => !s)}
                        >
                          {showPassword ? '隐藏' : '显示'}
                        </button>
                      ) : null
                    }
                  />
                );
              })}
            </dl>
          </section>

          <section className="card-pad flex flex-col gap-2">
            <header className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-ink-dim">
              Query <span className="normal-case text-ink">（{result.query.length} 项）</span>
            </header>
            {result.query.length === 0 ? (
              <p className="text-[12px] text-ink-dim">— 无 query 参数 —</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="border-b border-line/60 text-left text-ink-dim">
                      <th className="px-2 py-1 font-medium">Key</th>
                      <th className="px-2 py-1 font-medium">Original</th>
                      <th className="px-2 py-1 font-medium">Decoded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.query.map((q, i) => (
                      <tr
                        key={`${i}-${q.key}`}
                        className="border-b border-line/20 last:border-b-0 align-top"
                      >
                        <td className="px-2 py-1 font-mono text-accent">{q.key}</td>
                        <td className="px-2 py-1 font-mono text-ink-muted break-all">
                          {q.raw}
                        </td>
                        <td className="px-2 py-1 font-mono text-ink break-all">
                          {q.decoded}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card-pad flex flex-wrap gap-2">
            <CopyAction label="复制：拼回 URL" value={result.href ?? ''} />
            <CopyAction label="复制：JSON 结构" value={jsonSnapshot} />
          </section>
        </>
      )}
    </div>
  );
}

function FieldRow({
  label,
  value,
  rendered,
  extra,
}: {
  label: string;
  value: string;
  rendered: string;
  extra?: React.ReactNode;
}): JSX.Element {
  return (
    <>
      <dt className="text-ink-dim">{label}</dt>
      <dd className={clsx('flex items-center gap-2 font-mono text-ink min-w-0')}>
        {value.length === 0 ? (
          <span className="text-ink-dim">—</span>
        ) : (
          <span className="min-w-0 flex-1 break-all">{rendered}</span>
        )}
        {extra}
        {value.length > 0 && <CopyButton value={value} />}
      </dd>
    </>
  );
}

function CopyButton({ value }: { value: string }): JSX.Element {
  const [done, setDone] = useState(false);
  const onClick = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      window.setTimeout(() => setDone(false), 1000);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded p-1 text-ink-dim hover:bg-bg-soft hover:text-accent"
      title="复制"
    >
      {done ? (
        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
      ) : (
        <ClipboardCopy className="h-3 w-3" />
      )}
    </button>
  );
}

function CopyAction({ label, value }: { label: string; value: string }): JSX.Element {
  const [done, setDone] = useState(false);
  const onClick = async (): Promise<void> => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      window.setTimeout(() => setDone(false), 1000);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!value}
      className={clsx(
        'rounded border border-line px-2 py-1 text-xs',
        value
          ? 'text-ink-muted hover:border-accent/50 hover:text-accent'
          : 'cursor-not-allowed text-ink-dim',
      )}
    >
      {done ? '已复制' : label}
    </button>
  );
}
